/** @jsxImportSource @emotion/react */
'use memo'

import css from '@emotion/css/macro'
import { LoadingOutlined } from '@ant-design/icons'
import { forwardRef } from 'react'
import type { ButtonHTMLAttributes } from 'react'
import colors from '@sportninja/common/constants/appColors'
import { font, paragraphSmall, weightMedium } from 'src/components/css'

export type InlineTextButtonTone = 'primary' | 'neutral' | 'danger'

type InlineTextButtonProps = {
  text: string
  tone?: InlineTextButtonTone
  isLoading?: boolean
  /** Smaller variant for dense contexts such as a data-grid cell. */
  size?: 'default' | 'small'
  /**
   * Everything else lands on the native button. antd's Popconfirm / Tooltip /
   * Dropdown clone their trigger and inject handlers (and a ref) — swallowing
   * those would silently break the popup that wraps this.
   */
} & Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'type' | 'children'>

const TONE_COLORS: Record<
  InlineTextButtonTone,
  { base: string; hover: string }
> = {
  primary: { base: colors.PRIMARY, hover: colors.PRIMARY_LIGHT },
  neutral: { base: colors.NEUTRAL_0, hover: colors.PRIMARY },
  danger: { base: colors.ERROR_LIGHT, hover: colors.ERROR_200 },
}

/**
 * A borderless text action for dense rows — table cells, list rows, drawers —
 * where `HeaderActionButton`'s 40px pill would be far too heavy.
 *
 * It is a NATIVE button on purpose. The global antd Button token forces
 * `fontFamily: BarlowCondensed !important` (the condensed HEADING face), so an
 * antd `type='link'`/`type='text'` button renders its label in a display font
 * that reads as another tenant's branding, and `size='small'` shrinks it to 12px
 * on top of that. Action labels are body copy: paragraph face, paragraph size.
 */
const InlineTextButton = forwardRef<HTMLButtonElement, InlineTextButtonProps>(
  (
    {
      text,
      tone = 'primary',
      disabled = false,
      isLoading = false,
      size = 'default',
      ...rest
    },
    ref
  ) => {
    const isDisabled = disabled || isLoading
    const { base, hover } = TONE_COLORS[tone]

    return (
      <button
        {...rest}
        ref={ref}
        type='button'
        disabled={isDisabled}
        css={css`
          ${font.body}
          ${paragraphSmall}
        ${weightMedium}
        ${size === 'small' ? 'font-size: 12px;' : ''}
        display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 2px 6px;
          border: none;
          border-radius: 4px;
          background: none;
          white-space: nowrap;
          cursor: ${isDisabled ? 'not-allowed' : 'pointer'};
          color: ${isDisabled ? colors.NEUTRAL_100 : base};
          transition: color 0.15s ease-in-out,
            background-color 0.15s ease-in-out;

          &:hover:not(:disabled) {
            color: ${hover};
            background: rgba(255, 255, 255, 0.06);
          }
        `}
      >
        {isLoading ? <LoadingOutlined /> : null}
        {text}
      </button>
    )
  }
)

InlineTextButton.displayName = 'InlineTextButton'

export default InlineTextButton
